import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import swal from 'sweetalert';
import LoadingBar from "react-top-loading-bar";
import { useIsOnline } from 'react-use-is-online';
import {
    useDeleteTaxMutation,
    useEditUserTaxMutation,
    useGetTaxByIdQuery
} from '../../Features/taxesSlice';
import {
    ReceiptTaxIcon,
    ArrowLeftIcon,
    TrashIcon
} from '@heroicons/react/outline';

function EditTaxView() {
    const navigate = useNavigate();
    const { tax_id } = useParams();
    const [progress, setProgress] = useState(0);
    const { isOnline } = useIsOnline();

    const [errors, setErrors] = useState({
        name: '',
        type: '',
        rate: '',
    });

    const [input, setInput] = useState({});

    const [editTax] = useEditUserTaxMutation();
    const [deleteTax] = useDeleteTaxMutation();

    const {
        data: tax,
        isLoading,
        isSuccess,
    } = useGetTaxByIdQuery(tax_id);

    useEffect(() => {
        if (isLoading) {
            setProgress(80);
        }

        if (isSuccess) {
            setProgress(100);
        } else {
            setProgress(100);
        }

        if (isSuccess && tax.hasOwnProperty("data")) {
            setInput(tax.data);
        }

        return () => {};
    }, [tax, editTax, deleteTax]);

    const handleChange = (e) => {
        setInput({
            ...input,
            [e.target.name]: e.target.value
        });
    };

    const handDelete = async (e, id) => {
        if (!isOnline) {
            return swal(
                "No Internet connection",
                'Please check Internet connection and try again'
            );
        }

        if (window.confirm("Are you sure you want to delete this vehicle?")) {
            try {
                setProgress(80);

                const resp = await deleteTax(id).unwrap();

                if (resp.hasOwnProperty('success') == true) {
                    setProgress(100);
                    return navigate(-1);
                }
            } catch (err) {
                setProgress(100);
                swal("Error", err.message, 'error');
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isOnline) {
            return swal(
                "No Internet connection",
                'Please check Internet connection and try again'
            );
        }

        try {
            setProgress(80);

            const resp = await editTax(input).unwrap();

            if (resp.hasOwnProperty('success') == true) {
                setProgress(100);
                navigate(-1);
            }
        } catch (err) {
            setProgress(100);

            if (err.data.hasOwnProperty('errors') == true) {
                setErrors({
                    ...errors,
                    name: err.data.errors.name,
                    type: err.data.errors.type,
                    rate: err.data.errors.rate
                });
            } else if (err.data.hasOwnProperty('error') == true) {
                setInput({
                    ...input,
                    errorMassage: err.data.error
                });
            }
        }
    };

    const back = () => {
        if (!isOnline) {
            return swal(
                "No Internet connection",
                'Please check Internet connection and try again'
            );
        }

        navigate(-1);
    };

    return (
        <div className="w-full min-h-screen bg-gray-100 dark:bg-gray-950 pb-12">

            <LoadingBar
                color="#80b643"
                height="4px"
                progress={progress}
                onLoaderFinished={() => setProgress(0)}
            />

            <div className="w-full px-4 sm:px-6 lg:px-8 py-6">

                {/* Header */}
                <div className="flex items-center gap-3 mb-6">

                    <div className="w-11 h-11 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                        <ReceiptTaxIcon className="w-6 h-6 text-[#80b643]" />
                    </div>

                    <div>
                        <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                            Edit Tax
                        </h1>

                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            Update your tax information
                        </p>
                    </div>

                </div>

                {/* Main Card */}
                <div className="max-w-3xl mx-auto">

                    <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden">

                        {/* Loading State */}
                        {isLoading && (
                            <div className="flex flex-col items-center justify-center py-16">

                                <div className="w-10 h-10 border-4 border-gray-200 dark:border-gray-700 border-t-[#80b643] rounded-full animate-spin"></div>

                                <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                                    Loading tax information...
                                </p>

                            </div>
                        )}

                        {/* Form */}
                        {isSuccess && (
                            <form
                                onSubmit={handleSubmit}
                                autoComplete="off"
                            >

                                {/* Form Header */}
                                <div className="px-5 sm:px-6 py-5 border-b border-gray-200 dark:border-gray-800">

                                    <div className="flex items-center gap-3">

                                        <div className="w-9 h-9 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                                            <ReceiptTaxIcon className="w-5 h-5 text-[#80b643]" />
                                        </div>

                                        <div>
                                            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                                                Tax Information
                                            </h2>

                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                Update the details for this tax
                                            </p>
                                        </div>

                                    </div>

                                </div>

                                {/* Fields */}
                                <div className="p-5 sm:p-6">

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                                        {/* Name */}
                                        <div className="md:col-span-2">

                                            <label
                                                htmlFor="name"
                                                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
                                            >
                                                Name
                                            </label>

                                            <input
                                                className={`w-full px-3.5 py-2.5 rounded-lg border ${
                                                    errors.name
                                                        ? 'border-red-300 dark:border-red-700'
                                                        : 'border-gray-300 dark:border-gray-700'
                                                } bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm outline-none focus:ring-2 focus:ring-[#80b643]/20 focus:border-[#80b643] transition`}
                                                type="text"
                                                name="name"
                                                id="name"
                                                value={input.name || ''}
                                                onChange={handleChange}
                                                placeholder="Enter tax name"
                                            />

                                            {errors.name && (
                                                <p className="mt-1.5 text-xs font-medium text-red-600 dark:text-red-400">
                                                    {errors.name}
                                                </p>
                                            )}

                                        </div>

                                        {/* Rate */}
                                        <div>

                                            <label
                                                htmlFor="rate"
                                                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
                                            >
                                                Rate
                                            </label>

                                            <div className="relative">

                                                <input
                                                    className={`w-full px-3.5 py-2.5 pr-10 rounded-lg border ${
                                                        errors.rate
                                                            ? 'border-red-300 dark:border-red-700'
                                                            : 'border-gray-300 dark:border-gray-700'
                                                    } bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm outline-none focus:ring-2 focus:ring-[#80b643]/20 focus:border-[#80b643] transition`}
                                                    type="text"
                                                    name="rate"
                                                    id="rate"
                                                    value={input.rate || ''}
                                                    onChange={handleChange}
                                                    placeholder="Enter rate"
                                                />

                                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                                                    %
                                                </span>

                                            </div>

                                            {errors.rate && (
                                                <p className="mt-1.5 text-xs font-medium text-red-600 dark:text-red-400">
                                                    {errors.rate}
                                                </p>
                                            )}

                                        </div>

                                        {/* Type */}
                                        <div>

                                            <label
                                                htmlFor="type"
                                                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5"
                                            >
                                                Type
                                            </label>

                                            <select
                                                value={input.type}
                                                name="type"
                                                id="type"
                                                onChange={handleChange}
                                                className={`w-full px-3.5 py-2.5 rounded-lg border ${
                                                    errors.type
                                                        ? 'border-red-300 dark:border-red-700'
                                                        : 'border-gray-300 dark:border-gray-700'
                                                } bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm outline-none focus:ring-2 focus:ring-[#80b643]/20 focus:border-[#80b643] transition`}
                                            >
                                                <option
                                                    value={0}
                                                    className="text-gray-800"
                                                >
                                                    Included in the price
                                                </option>

                                                <option
                                                    value={1}
                                                    className="text-gray-800"
                                                >
                                                    Added to the price
                                                </option>

                                            </select>

                                            {errors.type && (
                                                <p className="mt-1.5 text-xs font-medium text-red-600 dark:text-red-400">
                                                    {errors.type}
                                                </p>
                                            )}

                                        </div>

                                    </div>

                                    {/* Delete Section */}
                                    <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-800">

                                        <div className="rounded-xl border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/20 p-4 sm:p-5">

                                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">

                                                <div className="flex items-start gap-3">

                                                    <div className="w-9 h-9 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                                                        <TrashIcon className="w-5 h-5 text-red-600 dark:text-red-400" />
                                                    </div>

                                                    <div>
                                                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                                                            Delete tax
                                                        </h3>

                                                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                                            You can delete this tax permanently.
                                                        </p>
                                                    </div>

                                                </div>

                                                <button
                                                    type="button"
                                                    onClick={(e) => handDelete(e, input.id)}
                                                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-red-200 dark:border-red-800 bg-white dark:bg-gray-900 text-red-600 dark:text-red-400 text-sm font-medium hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors w-full sm:w-auto"
                                                >
                                                    <TrashIcon className="w-4 h-4" />
                                                    Delete
                                                </button>

                                            </div>

                                        </div>

                                    </div>

                                </div>

                                {/* Actions */}
                                <div className="px-5 sm:px-6 py-4 bg-gray-50 dark:bg-gray-800/40 border-t border-gray-200 dark:border-gray-800">

                                    <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">

                                        <button
                                            type="button"
                                            onClick={back}
                                            className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-5 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                        >
                                            <ArrowLeftIcon className="w-4 h-4" />
                                            Cancel
                                        </button>

                                        <button
                                            type="submit"
                                            className="inline-flex items-center justify-center w-full sm:w-auto px-6 py-2.5 rounded-lg bg-[#80b643] hover:bg-[#719f3a] text-white text-sm font-medium shadow-sm transition-colors"
                                        >
                                            Save Changes
                                        </button>

                                    </div>

                                </div>

                            </form>
                        )}

                    </div>

                </div>

            </div>
        </div>
    );
}

export default EditTaxView;